import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, FolderKanban, Search, Calendar, Briefcase, ChevronDown } from 'lucide-react'
import { programsService, Program } from '../services/programs'
import { toast } from 'sonner'
import { getErrorMessage } from '../utils/apiTransformers'
import { useLabels } from '../contexts/LabelsContext'
import { StatusBadge } from '../components/StatusBadge'
import type { ApiErrorLike } from '../utils/apiError'

export default function ProgramList() {
  const navigate = useNavigate()
  const { label } = useLabels()
  const [programs, setPrograms] = useState<Program[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('all')

  useEffect(() => {
    fetchPrograms()
  }, [filterStatus])

  const fetchPrograms = async () => {
    setLoading(true)
    try {
      const params: Record<string, string | number | boolean> = {}
      if (filterStatus !== 'all') {
        params.status = filterStatus
      }
      const data = await programsService.list(params)
      setPrograms(data)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error fetching programs:', error)
      toast.error(getErrorMessage(error) || 'Failed to load programs')
    } finally {
      setLoading(false)
    }
  }

  const filteredPrograms = programs.filter((program) =>
    program.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="p-6 space-y-6 bg-[#f0f4f8] min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#102a43] via-[#486581] to-[#102a43] rounded-xl p-6 text-white shadow-lg border border-[#829ab1]/20 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl mb-2">{label('program', 'plural')}</h1>
              <p className="text-[#bcccdc]">Manage your {label('project').toLowerCase()} {label('program', 'plural').toLowerCase()}</p>
            </div>
            <button
              onClick={() => navigate('/program/create')}
              className="px-6 py-3 bg-[#DAA520] hover:bg-[#e58900] text-[#102a43] rounded-lg transition-colors font-medium flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create {label('program')}
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-4 mb-6">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#829ab1]" />
              <input
                type="text"
                placeholder="Search programs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#102a43]"
              />
            </div>
            <div className="relative min-w-[200px]">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full appearance-none pl-4 pr-10 py-2.5 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none bg-white text-[#102a43]"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="planning">Planning</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
                <option value="archived">Archived</option>
              </select>
              <ChevronDown className="w-4 h-4 text-[#829ab1] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Programs Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#486581] mx-auto mb-4"></div>
              <p className="text-[#486581]">Loading programs...</p>
            </div>
          </div>
        ) : filteredPrograms.length === 0 ? (
          <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-12 text-center">
            <FolderKanban className="w-16 h-16 text-[#829ab1] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#102a43] mb-2">No Programs Found</h3>
            <p className="text-[#829ab1] mb-6">
              {searchTerm || filterStatus !== 'all'
                ? 'No programs match your search criteria.'
                : 'No programs found. Create your first program to get started.'}
            </p>
            {!searchTerm && (
              <button
                onClick={() => navigate('/program/create')}
                className="px-6 py-3 bg-[#DAA520] hover:bg-[#e58900] text-[#102a43] rounded-lg transition-colors font-medium inline-flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Create Your First Program
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPrograms.map((program) => (
              <div
                key={program.id}
                onClick={() => navigate(`/program/${program.id}`)}
                className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
              >
                <div className="flex items-start justify-between mb-4">
                  <FolderKanban className="w-8 h-8 text-[#486581] flex-shrink-0" />
                  <StatusBadge entityType="program" status={program.status} />

                </div>
                <h3 className="text-lg font-semibold text-[#102a43] mb-2">{program.name}</h3>
                <p className="text-sm text-[#829ab1] line-clamp-2 mb-4">
                  {program.description || 'No description available'}
                </p>
                <div className="flex items-center gap-4 text-xs text-[#829ab1]">
                  {program.start_date && (
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      <span>{new Date(program.start_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  {program.portfolio_id && (
                    <div className="flex items-center gap-1">
                      <Briefcase className="w-3 h-3" />
                      <span>Portfolio #{program.portfolio_id}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

